import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminAssessmentStudio.css";

type Organization = {
  organizationId: string;
  name: string;
  type: string;
};

type QuickAssessment = {
  versionId: string;
  title: string;
  targetPopulation?: string | null;
  gradeMin?: number | null;
  gradeMax?: number | null;
  estimatedMinutes?: number | null;
  publishedAt?: string | null;
  itemCount: number;
  assignedCount: number;
  completedCount: number;
};

type Catalog = {
  organizations: Organization[];
  quickAssessments: QuickAssessment[];
};

type Template = {
  key: string;
  name: string;
  description: string;
  target: string;
  questions: string[];
};

const RS_ASSESSMENT_URL = "https://rscds.co.kr";

const TEMPLATES: Template[] = [
  {
    key: "career",
    name: "진로준비도 체크",
    description: "학생이 자신의 진로를 얼마나 탐색하고 준비하고 있는지 가볍게 확인합니다.",
    target: "중학생",
    questions: [
      "나는 내가 좋아하는 활동과 관심 분야를 알고 있다.",
      "나는 내가 잘하는 점과 강점을 설명할 수 있다.",
      "나는 관심 있는 직업이나 진로 분야를 찾아본 적이 있다.",
      "나는 진로와 관련해 앞으로 해보고 싶은 활동이 있다.",
      "나는 진로를 위해 지금 실천할 수 있는 작은 행동을 알고 있다."
    ]
  },
  {
    key: "self",
    name: "자기이해 체크",
    description: "흥미·강점·가치관을 스스로 어느 정도 이해하고 있는지 간단히 살펴봅니다.",
    target: "중학생",
    questions: [
      "나는 어떤 활동을 할 때 즐거운지 알고 있다.",
      "나는 자연스럽게 잘하는 것이 무엇인지 알고 있다.",
      "나는 선택할 때 중요하게 생각하는 가치가 무엇인지 알고 있다.",
      "나는 나의 장점과 보완할 점을 구체적으로 말할 수 있다.",
      "나는 나에게 잘 맞는 환경이나 활동 방식을 알고 있다."
    ]
  },
  {
    key: "learning",
    name: "학습습관 체크",
    description: "계획·집중·복습·도움요청 등 기본 학습습관을 빠르게 확인합니다.",
    target: "중학생",
    questions: [
      "나는 해야 할 공부를 미리 계획하는 편이다.",
      "나는 공부할 때 방해요인을 줄이고 집중하려고 한다.",
      "나는 배운 내용을 다시 확인하거나 복습한다.",
      "나는 모르는 것이 있을 때 질문하거나 도움을 요청한다.",
      "나는 학습 결과를 돌아보고 다음 방법을 조정한다."
    ]
  },
  {
    key: "program",
    name: "프로그램 경험 체크",
    description: "진로수업·체험·캠프 종료 후 학생의 참여감과 도움 정도를 간단히 확인합니다.",
    target: "중학생",
    questions: [
      "이번 활동에 적극적으로 참여했다.",
      "이번 활동은 나를 이해하는 데 도움이 되었다.",
      "이번 활동은 진로를 생각하는 데 도움이 되었다.",
      "이번 활동에서 새롭게 알게 된 것이 있다.",
      "비슷한 활동에 다시 참여하고 싶다."
    ]
  }
];

const GRADE_OPTIONS = [
  ["", "전체"],
  ["1", "초1"], ["2", "초2"], ["3", "초3"], ["4", "초4"], ["5", "초5"], ["6", "초6"],
  ["7", "중1"], ["8", "중2"], ["9", "중3"],
  ["10", "고1"], ["11", "고2"], ["12", "고3"]
] as const;

function gradeLabel(value?: number | null) {
  if (!value) return "";
  return GRADE_OPTIONS.find(([key]) => Number(key) === value)?.[1] || String(value);
}

function gradeRange(item: QuickAssessment) {
  const min = gradeLabel(item.gradeMin);
  const max = gradeLabel(item.gradeMax);
  if (min && max) return min === max ? min : min + "~" + max;
  if (min) return min + " 이상";
  if (max) return max + " 이하";
  return "전체";
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("ko-KR");
}

export function AdminAssessmentStudio({ client }: { client: PlatformClient }) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [ownerId, setOwnerId] = useState("");
  const [name, setName] = useState(TEMPLATES[0].name);
  const [description, setDescription] = useState(TEMPLATES[0].description);
  const [targetPopulation, setTargetPopulation] = useState(TEMPLATES[0].target);
  const [gradeMin, setGradeMin] = useState("7");
  const [gradeMax, setGradeMax] = useState("9");
  const [questions, setQuestions] = useState<string[]>(TEMPLATES[0].questions);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [lowLabel, setLowLabel] = useState("낮은 편");
  const [lowSummary, setLowSummary] = useState("현재 응답에서는 이 특성이 비교적 낮게 나타났습니다.");
  const [midLabel, setMidLabel] = useState("보통");
  const [midSummary, setMidSummary] = useState("현재 응답에서는 이 특성이 중간 수준으로 나타났습니다.");
  const [highLabel, setHighLabel] = useState("높은 편");
  const [highSummary, setHighSummary] = useState("현재 응답에서는 이 특성이 비교적 높게 나타났습니다.");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setError("");
    client.rpc("admin_quick_assessment_catalog").then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setError(error.message);
        return;
      }

      const next = data as Catalog;
      setCatalog(next);
      setOwnerId((current) => {
        if (next.organizations.some((item) => item.organizationId === current)) return current;
        return next.organizations.find((item) => item.type === "PLATFORM")?.organizationId
          || next.organizations[0]?.organizationId
          || "";
      });
    });

    return () => { active = false; };
  }, [client, refresh]);

  const cleanQuestions = useMemo(
    () => questions.map((item) => item.trim()).filter(Boolean),
    [questions]
  );

  const estimatedMinutes = Math.max(3, Math.ceil(cleanQuestions.length / 3));
  const validGrades = !gradeMin || !gradeMax || Number(gradeMax) >= Number(gradeMin);
  const canCreate = Boolean(
    ownerId
    && name.trim()
    && validGrades
    && cleanQuestions.length >= 3
    && cleanQuestions.length <= 30
  );

  function useTemplate(template: Template) {
    setName(template.name);
    setDescription(template.description);
    setTargetPopulation(template.target);
    setQuestions([...template.questions]);
    setError("");
    setMessage("");
  }

  function updateQuestion(index: number, value: string) {
    setQuestions((current) => current.map((item, itemIndex) => itemIndex === index ? value : item));
  }

  async function createQuickAssessment() {
    if (!canCreate) return;
    setBusy(true);
    setError("");
    setMessage("");

    const { data, error } = await client.rpc("admin_create_quick_assessment", {
      p_owner_organization_id: ownerId,
      p_name: name.trim(),
      p_description: description.trim() || null,
      p_target_population: targetPopulation.trim() || null,
      p_grade_min: gradeMin ? Number(gradeMin) : null,
      p_grade_max: gradeMax ? Number(gradeMax) : null,
      p_questions: cleanQuestions,
      p_low_label: lowLabel.trim(),
      p_low_summary: lowSummary.trim(),
      p_mid_label: midLabel.trim(),
      p_mid_summary: midSummary.trim(),
      p_high_label: highLabel.trim(),
      p_high_summary: highSummary.trim()
    });

    setBusy(false);

    if (error) {
      setError(error.message);
      return;
    }

    const created = data as { title?: string } | null;
    setMessage("“" + (created?.title || name.trim()) + "”을 만들었습니다. Center → 진로검사에서 바로 학생에게 배정할 수 있습니다.");
    setRefresh((value) => value + 1);
  }

  return (
    <section className="assessment-hub-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">RS Assessment Hub</p>
          <h2>심리검사</h2>
          <p className="route-description">전문검사는 기존 RS 플랫폼을 사용하고, Admin에서는 학교용 간단 체크만 빠르게 만듭니다.</p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="assessment-hub-success">{message}</p>}

      <div className="assessment-hub-choice-grid">
        <article className="assessment-hub-card professional">
          <div className="assessment-hub-mark">RS</div>
          <div>
            <p className="eyebrow">전문 심리검사</p>
            <h3>RS 심리검사 플랫폼</h3>
            <p>기존 검사자산, 전문검사 실시, 전문 결과해석은 새로 만들지 않고 기존 플랫폼을 그대로 사용합니다.</p>
            <div className="assessment-hub-tags"><span>전문검사</span><span>기존 검사자산</span><span>전문 결과해석</span></div>
          </div>
          <a href={RS_ASSESSMENT_URL} target="_blank" rel="noreferrer">rscds.co.kr 열기 ↗</a>
        </article>

        <article className="assessment-hub-card quick">
          <div className="assessment-hub-mark">5</div>
          <div>
            <p className="eyebrow">학교용 간단 체크</p>
            <h3>5점 척도로 바로 만들기</h3>
            <p>검사명과 문항만 입력하면 5점 응답, 평균 채점, 3단계 결과가 자동 구성됩니다.</p>
            <div className="assessment-hub-tags"><span>3~30문항</span><span>자동 채점</span><span>Center 즉시 배정</span></div>
          </div>
          <b className="assessment-hub-badge">비표준화 도구</b>
        </article>
      </div>

      <div className="assessment-hub-boundary">
        <strong>구분 원칙</strong>
        <span>진단·표준화·전문 해석이 필요한 검사는 RS 심리검사 플랫폼을 사용합니다.</span>
        <span>아래 간단 체크는 교육활동·자기이해·프로그램 피드백용 참고자료입니다.</span>
      </div>

      <section className="quick-assessment-builder">
        <header className="quick-builder-head">
          <div>
            <p className="eyebrow">Quick Check Builder</p>
            <h3>간단 체크검사 만들기</h3>
            <p>템플릿을 고르고 문항만 다듬으면 됩니다. 규준·Validation·전문가 승인 화면은 사용하지 않습니다.</p>
          </div>
          <div className="quick-builder-time"><span>예상 응답시간</span><strong>약 {estimatedMinutes}분</strong></div>
        </header>

        <div className="quick-template-row">
          {TEMPLATES.map((template) => (
            <button key={template.key} type="button" onClick={() => useTemplate(template)}>
              <strong>{template.name}</strong>
              <small>{template.questions.length}문항</small>
            </button>
          ))}
        </div>

        <div className="quick-builder-section">
          <div className="quick-section-title"><span>1</span><div><strong>기본정보</strong><small>대상과 사용 목적만 정합니다.</small></div></div>
          <div className="quick-basic-grid">
            <label>운영기관<select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
              {(catalog?.organizations || []).map((organization) => (
                <option key={organization.organizationId} value={organization.organizationId}>{organization.name}</option>
              ))}
            </select></label>
            <label>검사명<input value={name} onChange={(event) => setName(event.target.value)} /></label>
            <label>대상<input value={targetPopulation} onChange={(event) => setTargetPopulation(event.target.value)} /></label>
            <label>최소 학년<select value={gradeMin} onChange={(event) => setGradeMin(event.target.value)}>
              {GRADE_OPTIONS.map(([value, label]) => <option key={"min-" + value} value={value}>{label}</option>)}
            </select></label>
            <label>최대 학년<select value={gradeMax} onChange={(event) => setGradeMax(event.target.value)}>
              {GRADE_OPTIONS.map(([value, label]) => <option key={"max-" + value} value={value}>{label}</option>)}
            </select></label>
            <label className="quick-wide">안내문<textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
          </div>
          {!validGrades && <p className="error">최대 학년은 최소 학년보다 낮을 수 없습니다.</p>}
        </div>

        <div className="quick-builder-section">
          <div className="quick-section-title"><span>2</span><div><strong>문항</strong><small>모든 문항은 5점 척도로 응답합니다.</small></div><b>{questions.length}문항</b></div>
          <div className="quick-question-list">
            {questions.map((question, index) => (
              <div className="quick-question-row" key={"question-" + index}>
                <span>{index + 1}</span>
                <textarea rows={2} value={question} onChange={(event) => updateQuestion(index, event.target.value)} placeholder="문항을 입력하세요." />
                <button type="button" disabled={questions.length <= 3} onClick={() => setQuestions((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
              </div>
            ))}
          </div>
          <button className="quick-add-question" type="button" disabled={questions.length >= 30} onClick={() => setQuestions((current) => [...current, ""])}>+ 문항 추가</button>
        </div>

        <div className="quick-builder-section">
          <button className="quick-advanced-toggle" type="button" onClick={() => setAdvancedOpen((value) => !value)}>
            <span><strong>3. 결과 문구</strong><small>기본값 그대로 사용해도 됩니다.</small></span>
            <b>{advancedOpen ? "접기 −" : "수정 +"}</b>
          </button>
          {advancedOpen && (
            <div className="quick-result-grid">
              <label><span>낮은 구간</span><input value={lowLabel} onChange={(e) => setLowLabel(e.target.value)} /><textarea rows={3} value={lowSummary} onChange={(e) => setLowSummary(e.target.value)} /></label>
              <label><span>중간 구간</span><input value={midLabel} onChange={(e) => setMidLabel(e.target.value)} /><textarea rows={3} value={midSummary} onChange={(e) => setMidSummary(e.target.value)} /></label>
              <label><span>높은 구간</span><input value={highLabel} onChange={(e) => setHighLabel(e.target.value)} /><textarea rows={3} value={highSummary} onChange={(e) => setHighSummary(e.target.value)} /></label>
            </div>
          )}
        </div>

        <footer className="quick-builder-footer">
          <div><strong>생성 즉시 사용 가능</strong><span>Center → 진로검사에서 학생에게 바로 보낼 수 있습니다.</span></div>
          <button className="primary" disabled={!canCreate || busy} onClick={createQuickAssessment}>{busy ? "만드는 중…" : "검사 만들고 사용 시작"}</button>
        </footer>
      </section>

      <section className="quick-assessment-library">
        <div className="panel-title"><div><strong>만든 간단 체크</strong><span>비표준화 학교용 체크검사</span></div><b>{catalog?.quickAssessments.length || 0}개</b></div>
        <div className="quick-library-grid">
          {(catalog?.quickAssessments || []).map((assessment) => (
            <article key={assessment.versionId}>
              <div className="quick-library-head"><span>간단 체크</span><small>{formatDate(assessment.publishedAt)}</small></div>
              <h4>{assessment.title}</h4>
              <p>{assessment.targetPopulation || "대상 미지정"} · {gradeRange(assessment)} · 약 {assessment.estimatedMinutes || "-"}분</p>
              <div className="quick-library-stats">
                <span><b>{assessment.itemCount}</b>문항</span>
                <span><b>{assessment.assignedCount}</b>배정</span>
                <span><b>{assessment.completedCount}</b>완료</span>
              </div>
            </article>
          ))}
          {!catalog?.quickAssessments.length && (
            <div className="quick-library-empty"><strong>아직 만든 간단 체크가 없습니다.</strong><p>위 템플릿에서 하나를 골라 바로 만들어보세요.</p></div>
          )}
        </div>
      </section>
    </section>
  );
}
